
import React from "react";
import classnames from "classnames";
import {
  Alert,
  Button,
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  Label,
  FormGroup,
  InputGroup,
  Form,
  Input,
  CardTitle,
  CardText,
  CardImg,
} from "reactstrap";
import TableWrapper from "@/utils/TableWrapper";
import type { TableWrapperProps } from "@/utils/TableWrapper";
import type { SitePermission } from "@/services/userServices/useCurrentPermission";
import type { BlogAuthorCard } from "./SitePostsView";

/**
 * SCRUM-1150: the post-selection list renders through TableWrapper, so this view
 * no longer carries a search box, a pager or a hand-rolled `<Table>`. The blog
 * form (title, description, Add/Update) and the author side card are untouched —
 * only the list chrome moved.
 *
 * Known consequence, recorded rather than discovered: the replaced input had
 * `placeholder="Search by post title"`; TableWrapper's built-in box searches every
 * column and carries no accessible name. Fixing that belongs to TableWrapper,
 * which SCRUM-1026 is explicit about not modifying (SCRUM-1149 tracks it).
 */
export type SiteBlogViewProps = {
  title: string;
  description: string;
  validation: { title: boolean };
  viewAddButton: boolean;
  viewUpdateButton: boolean;
  permission: SitePermission;
  hasNoBlog: boolean;
  posts: unknown[];
  columns: TableWrapperProps<unknown>["columns"];
  viewAuthors: boolean;
  author: BlogAuthorCard | null;
  onChangeTitle: React.ChangeEventHandler<HTMLInputElement>;
  onChangeDescription: React.ChangeEventHandler<HTMLInputElement>;
  onClickAdd: React.MouseEventHandler<HTMLButtonElement>;
  onClickUpdate: React.MouseEventHandler<HTMLButtonElement>;
  closeViewAuthor: () => void;
  /** The router Link the Posts button renders as (tag={Link}). */
  Link: React.ElementType;
};

export default function SiteBlogView({
  title,
  description,
  validation,
  viewAddButton,
  viewUpdateButton,
  permission,
  hasNoBlog,

  posts,
  columns,
  viewAuthors,
  author,
  onChangeTitle,
  onChangeDescription,
  onClickAdd,
  onClickUpdate,
  closeViewAuthor,
  Link,
}: SiteBlogViewProps) {
  return (
    <Container className="mt--6" fluid>
      <Row>
        <Col>
          <Card>
            <CardHeader>
              <Row className="align-items-center">
                <Col xs="8">
                  <h5 className="h3 mb-0">Manage Site Blog</h5>
                </Col>
                <Col className="text-right" xs="4">
                  <Button color="default" size="sm" to="/admin/posts" tag={Link}>
                    Posts
                  </Button>
                </Col>
              </Row>
            </CardHeader>

            <CardBody>
              {/* SCRUM-951: persistent inline empty state when this site has no
                  blog yet (info = expected-empty, matching the TranslatePages
                  convention). Rendered above the form so "below" reads true. */}
              {hasNoBlog ? (
                <Alert color="info" className="text-center" data-testid="blog-empty-state">
                  <strong>No blog has been created for this site yet.</strong>{" "}
                  {permission.publish || permission.edit
                    ? "Fill in a title and description below, then click Add."
                    : "Ask an editor to create one."}
                </Alert>
              ) : null}
              <Form role="form" onSubmit={(e) => e.preventDefault()}>
                {/* Title */}
                <FormGroup className="row">
                  <Label className="form-control-label" md="2">
                    Title
                  </Label>
                  <Col md="7">
                    <InputGroup className={classnames("input-group-merge")}>
                      <Input
                        placeholder="Insert Blog Title"
                        name="title"
                        type="text"
                        value={title}
                        onChange={onChangeTitle}
                        invalid={!validation.title}
                      />
                    </InputGroup>
                    {!validation.title ? (
                      <InputGroup className="invalid-feedback">
                        Required State.
                      </InputGroup>
                    ) : (
                      ""
                    )}
                  </Col>
                </FormGroup>

                {/* Description */}
                <FormGroup className="row">
                  <Label className="form-control-label" md="2">
                    Description
                  </Label>
                  <Col md="7">
                    <InputGroup className={classnames("input-group-merge")}>
                      <Input
                        placeholder="Insert Blog Description"
                        name="description"
                        type="textarea"
                        value={description}
                        onChange={onChangeDescription}
                        required
                      />
                    </InputGroup>

                    <div className="text-right mt-3">
                      {viewAddButton && (permission.publish || permission.edit) ? (
                        <Button color="primary" onClick={onClickAdd} size="sm">
                          Add
                        </Button>
                      ) : (
                        ""
                      )}
                      {viewUpdateButton &&
                      (permission.publish || permission.edit) ? (
                        <Button color="primary" onClick={onClickUpdate} size="sm">
                          Update
                        </Button>
                      ) : (
                        ""
                      )}
                    </div>
                  </Col>
                </FormGroup>

                {/* Select Posts label */}
                <FormGroup className="row">
                  <Label className="form-control-label" md="2">
                    Select Posts
                  </Label>
                </FormGroup>
              </Form>

              <TableWrapper data={posts} columns={columns} />
            </CardBody>
          </Card>
        </Col>

        {viewAuthors ? (
          <Col lg="4">
            <Card>
              <CardImg alt="..." src={author?.avatar} top />
              <CardBody>
                <CardTitle className="h2 mb-0">{author?.name}</CardTitle>
                <small className="text-muted">{author?.role}</small>
                <CardText className="mt-4">{author?.description}</CardText>
                <Row>
                  <Col lg="6">
                    <div className="text-left">
                      <Button className="px-0" color="link" onClick={closeViewAuthor}>
                        close
                      </Button>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        ) : (
          ""
        )}
      </Row>
    </Container>
  );
}
